import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { memo, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router';
import routeConfiguration from '../../../routeConfiguration';
import { petrol } from '../../../styles/mui/colors';
import { ensureCurrentUser } from '../../../util/data';
import useAppSelector from '../../../util/hooks/useAppSelector';
import usePassPlus from '../../../util/hooks/usePassPlus';
import { createResourceLocatorString } from '../../../util/routes';
import Link from '../../Links/Link/Link';
import LanguageMenu from '../TopBar/TopbarDesktop/LanguageMenu';

const LoyaltyPointsSection = ({ points, handleLoyaltyPage }) => (
  <>
    <Divider />
    <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
      <Typography sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }} variant="caption">
        <FormattedMessage
          id="TopbarMobileMenu.yourPoints"
          values={{ points }}
        />
      </Typography>
      <Button color="secondary" onClick={handleLoyaltyPage} size="small" variant="text">
        <FormattedMessage id="TopbarMobileMenu.toLoyaltyPage" />
      </Button>
    </Stack>
  </>
);


const SectionLoginComponent = ({ onClose }) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const [hasPlusPass] = usePassPlus();

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const currentUser = useAppSelector((state) => state.user.currentUser);
  const user = ensureCurrentUser(currentUser);
  const displayName = user.attributes.profile.firstName;
  const profileImageUrl = user.profileImage?.attributes?.variants['square-small']?.url;
  const TopbarMobileMenuGreetingValues = useMemo(() => ({ displayName }), [displayName]);

  const avatarStyle = hasPlusPass ? { border: `3px solid ${petrol.main}` } : null;

  const handleLogin = useCallback(() => {
    onClose();
    const routeName = 'SigninPage';
    let pathParams = {};

    const url = createResourceLocatorString(
      routeName,
      routeConfiguration(),
      pathParams,
      {},
      intl.locale,
    );
    navigate(url);
  }, [onClose, navigate, intl]);


  const topBar = (
    <Stack alignItems="center" direction="row" justifyContent="space-between">
      {isAuthenticated ? (
        <Badge
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          badgeContent={hasPlusPass ? 'Plus' : null}
          color="primary"
          overlap="circular"
        >
          <Avatar src={profileImageUrl} sx={{ width: 40, height: 40, ...avatarStyle }} />
        </Badge>
      ) : null}
      <Typography variant="h5">
        {isAuthenticated ? (
          <FormattedMessage
            id="TopbarMobileMenu.greeting"
            values={TopbarMobileMenuGreetingValues}
          />
        ) : (
          <FormattedMessage id="TopbarMobileMenu.unauthorizedGreeting" />
        )}
      </Typography>
      <LanguageMenu />
    </Stack>
  );

  const handleLoyaltyPage = () => {
    onClose();
    navigate(
      createResourceLocatorString('LoyaltyProgrammePage', routeConfiguration(), {}, {}, intl.locale),
    );
  };

  const loyalty = currentUser ?
    <LoyaltyPointsSection handleLoyaltyPage={handleLoyaltyPage}
                          points={currentUser?.attributes?.profile?.privateData?.loyalty?.points || 0} /> : null;


  return (
    <Stack px={4} spacing={1}>
      {topBar}
      {loyalty}
      {!isAuthenticated ? (
        <Stack direction="column" spacing={2} width="100%">
          <Button
            color="primary"
            endIcon={<ChevronRightIcon />}
            mt={2}
            onClick={handleLogin}
            variant="contained"
          >
            <FormattedMessage id="TopbarMobileMenu.loginLink" />
          </Button>
          <Button
            component={Link}
            onClick={onClose}
            target="_self"
            to={createResourceLocatorString(
              'StoryblokPage',
              routeConfiguration(),
              { slug: 'gastgeber-werden' },
              {},
              intl.locale,
            )}
            variant="outlined"
          >
            <FormattedMessage id="Footer.toNewListingPage" />
          </Button>
        </Stack>
      ) : null}
    </Stack>
  );
};

SectionLoginComponent.displayName = 'SectionLogin';
const SectionLogin = memo(SectionLoginComponent);
export default SectionLogin;
